'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import omit from 'lodash/omit';
import { useTranslation } from 'react-i18next';

// UI lib dependencies
import { Stack, Text, ActionIcon, Center, Group } from '@mantine/core';
import {
  Dropzone,
  DropzoneFullScreenProps,
  DropzoneProps,
} from '@mantine/dropzone';

// Local helpers & utils
import { getMaxUploadSize } from '@helpers/size';

// Upload utilities
import useUploader from '@services/upload/useUploader';

// Types
import {
  ignoredExtensions,
  supportedNonStandardExtensions,
} from '@app-types/blackroom-app/resourceMimeType';
import { UploadAreaProps } from './types';

// Assets
import {
  IconCloudUpload as IconUploadArea,
  IconUpload as IconUploadIdle,
  IconX as IconUploadReject,
} from '@tabler/icons-react';

// Style
import * as classes from './styles/index.css';
import * as dropzoneStylesAPI from './styles/dropzoneStylesAPI.css';
import { FONT_WEIGHT } from '@ui-kit/fonts';
import { palette } from '@ui-kit/colors';
import { getFileExtension } from '@app/shared/helpers/file';
import {
  validateUploadFileAsync,
  validateUploadFileSync,
} from './utils/validateUploadFile';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function UploadArea({
  asPageUploader = false,
  onAddedFiles = () => {},
  ...props
}: UploadAreaProps) {
  /* ********************************** HOOKS ********************************* */

  // Upload utilities
  const { invokePromptExceededUploadSize, invokePromptInvalidFiles } =
    useUploader();

  // Localization
  const { t } = useTranslation('Resource');

  /* ******************************** CALLBACKS ******************************* */

  /* ***************************** LOCAL VARIABLES **************************** */

  const maxSize = getMaxUploadSize();

  const sharedDropzoneProps: Omit<
    DropzoneProps & DropzoneFullScreenProps,
    'children'
  > = {
    classNames: { ...dropzoneStylesAPI },
    className: classes.uploadArea,
    p: 0,
    maxSize,
    getFilesFromEvent: async (event) => {
      const items = (event as DragEvent).dataTransfer?.items;
      if (!items?.length) return [];
      return Array.from(items);
    },
    validator: (file) => {
      if (
        validateUploadFileSync(file as unknown as DataTransferItem).validation
      ) {
        return null;
      }
      return {
        code: 'invalid-type',
        message: 'Invalid resource type',
      };
    },
    async onDropAny(_acceptedFiles, _fileRejections) {
      // Process rejections - each rejection has { file, errors } structure
      const rejections = _fileRejections as unknown as Array<{
        file: DataTransferItem;
        errors: Array<{ code: string; message: string }>;
      }>;

      // Get entries for rejected files, preserving error info
      const rejectedWithErrors = rejections
        .map((r) => {
          const entry = r.file?.webkitGetAsEntry?.();
          return entry ? { entry, errors: r.errors } : null;
        })
        .filter((e) => e !== null);

      const largeFiles = rejectedWithErrors
        .filter((r) => r.errors.some((err) => err.code === 'file-too-large'))
        .map((r) => r.entry);

      const invalidFileTypeRejections = rejectedWithErrors
        .filter((r) => r.errors.some((err) => err.code === 'invalid-type'))
        .map((r) => r.entry);

      const supportedNonStandardFiles = invalidFileTypeRejections.filter(
        (entry) =>
          supportedNonStandardExtensions.includes(getFileExtension(entry.name)),
      );

      // Use async validation with real mimetype detection
      const acceptedItems = _acceptedFiles as unknown as DataTransferItem[];

      const itemsWithEntries = acceptedItems.map((item) => ({
        item,
        entry: item?.webkitGetAsEntry?.() ?? null,
      }));

      const validationResults = await Promise.all(
        itemsWithEntries.map(async ({ item, entry }) => {
          if (item === null) {
            return { entry, isValid: false };
          }
          const validationResult = await validateUploadFileAsync(item);
          const { validation, extension } = validationResult;
          const isValid = extension
            ? validation && !ignoredExtensions.includes(extension)
            : validation;
          return { entry, isValid };
        }),
      );

      const finalEntries = validationResults
        .filter((r) => r.isValid && r.entry !== null)
        .map((r) => r.entry)
        .concat(supportedNonStandardFiles) as FileSystemEntry[];

      if (largeFiles.length > 0) {
        invokePromptExceededUploadSize(largeFiles);
      } else if (invalidFileTypeRejections.length > 0) {
        invokePromptInvalidFiles(
          {
            invalidFilesTypes: invalidFileTypeRejections,
            invalidFilesEmpty: [],
          },
          () => {
            onAddedFiles(finalEntries);
          },
        );
      } else {
        onAddedFiles(finalEntries);
      }
    },
    onDrop: () => {},
    color: 'blackroomGray',
  };

  /* ******************************** RENDERING ******************************* */

  if (asPageUploader) {
    return (
      <Dropzone.FullScreen
        {...omit(sharedDropzoneProps, 'classNames')}
        active
        inputProps={{ 'aria-label': t('UploadArea.placeholder') }}
      >
        <Group
          justify="center"
          gap="xl"
          mih={220}
          style={{ pointerEvents: 'none' }}
        >
          <Dropzone.Accept>
            <IconUploadArea
              size="3.2rem"
              stroke={1.5}
              color={palette.blackroomBlue}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconUploadReject
              size="3.2rem"
              stroke={1.5}
              color={palette.blackroomRed}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconUploadIdle
              size="3.2rem"
              stroke={1.5}
              color={palette.blackroomLightGray2}
            />
          </Dropzone.Idle>
          <Center h="80vh">
            <Text size="1.7rem" c="dimmed">
              {t('UploadArea.placeholder')}
            </Text>
          </Center>
        </Group>
      </Dropzone.FullScreen>
    );
  }

  return (
    <Dropzone
      {...sharedDropzoneProps}
      {...props}
      inputProps={{ 'aria-label': t('UploadArea.placeholder') }}
    >
      <Center h={props.h}>
        <Stack justify="center" align="center" gap="xs">
          <ActionIcon variant="transparent" color="blackroomBlue">
            <IconUploadArea />
          </ActionIcon>
          <Text size="sm" fw={FONT_WEIGHT.lighter} ta="center">
            {t('UploadArea.placeholder')}
          </Text>
        </Stack>
      </Center>
    </Dropzone>
  );
}

export default UploadArea;
